import axios from '@/src/api/axios_init'
import { queryKeys } from '@/src/api/queryKeys'
import type { DataCompletenessControllerGetDataCompletenessData, GetDataCompletenessResponseDto } from '@/src/api/types/generated-types'
import type { DataCompletenessPdfAccess } from '@/src/components/DataCompletenessIndicator/DataCompletenessAccessContext'
import { useQuery } from '@tanstack/react-query'
import { ONE_MINUTE_IN_MS } from '../utils/constants'
import { normalizeDataCompletenessDateRange } from '../utils/dataCompletenessDate.utils'

// Every field of this request now comes from the generated client: itemLevel/entityType via
// app-backend#2513, includeElectricitySpotPrices already. The local intersection this type
// used to carry is gone.
export type DataCompletenessItemLevel = NonNullable<DataCompletenessControllerGetDataCompletenessData['itemLevel']>
export type DataCompletenessEntityType = NonNullable<DataCompletenessControllerGetDataCompletenessData['entityType']>

export type DataCompletenessRequestData = DataCompletenessControllerGetDataCompletenessData

type DataCompletenessScopeRequestData = Pick<
  DataCompletenessRequestData,
  'buildingIds' | 'companyId' | 'customTimeseriesIds' | 'deviceIds' | 'includeElectricitySpotPrices' | 'locationIds'
>

export const hasDataCompletenessScope = (request: DataCompletenessScopeRequestData) =>
  !!request.companyId ||
  !!request.customTimeseriesIds?.length ||
  !!request.deviceIds?.length ||
  !!request.buildingIds?.length ||
  !!request.locationIds?.length ||
  request.includeElectricitySpotPrices === true

const hasDataCompletenessDateRange = (request: DataCompletenessRequestData) => !!request.startDate && !!request.endDate

const appendArrayParam = (params: URLSearchParams, key: string, values?: Array<string | number>) => {
  for (const value of values ?? []) {
    params.append(key, String(value))
  }
}

export const buildDataCompletenessSearchParams = (request: DataCompletenessRequestData) => {
  const params = new URLSearchParams()
  appendArrayParam(params, 'deviceIds', request.deviceIds)
  appendArrayParam(params, 'customTimeseriesIds', request.customTimeseriesIds)
  appendArrayParam(params, 'buildingIds', request.buildingIds)
  appendArrayParam(params, 'locationIds', request.locationIds)

  if (request.companyId != null) params.set('companyId', String(request.companyId))
  if (request.startDate) params.set('startDate', request.startDate)
  if (request.endDate) params.set('endDate', request.endDate)
  if (request.energyType) params.set('energyType', request.energyType)
  if (request.onlyMainMeters != null) params.set('onlyMainMeters', String(request.onlyMainMeters))
  if (request.includeElectricitySpotPrices != null) {
    params.set('includeElectricitySpotPrices', String(request.includeElectricitySpotPrices))
  }
  if (request.itemLevel) params.set('itemLevel', request.itemLevel)
  if (request.entityType) params.set('entityType', request.entityType)
  if (request.includeIntervals != null) params.set('includeIntervals', String(request.includeIntervals))

  return params
}

const fetchDataCompleteness = async (request: DataCompletenessRequestData) => {
  const params = buildDataCompletenessSearchParams(request)
  const response = await axios.get<GetDataCompletenessResponseDto>(`/data-completeness?${params.toString()}`)
  return response.data
}

const fetchPdfDataCompleteness = async (request: DataCompletenessRequestData, pdfAccess: DataCompletenessPdfAccess) => {
  const params = new URLSearchParams({ token: pdfAccess.token })
  buildDataCompletenessSearchParams(request).forEach((value, key) => {
    params.append(key, value)
  })

  const response = await axios.get<GetDataCompletenessResponseDto>(
    `/custom-reports/pdf-access/report/${pdfAccess.reportId}/data-completeness?${params.toString()}`
  )
  return response.data
}

export const useDataCompleteness = (
  request: DataCompletenessRequestData,
  pdfAccess?: DataCompletenessPdfAccess | null,
  options: { enabled?: boolean } = {}
) => {
  const { enabled = true } = options
  const normalizedDateRange = normalizeDataCompletenessDateRange(request.startDate, request.endDate)
  const normalizedRequest: DataCompletenessRequestData = {
    ...request,
    startDate: normalizedDateRange.startDate,
    endDate: normalizedDateRange.endDate,
  }
  return useQuery<GetDataCompletenessResponseDto, Error>({
    queryKey: [...queryKeys.dataCompleteness.getDataCompleteness.toKeyWithArgs(normalizedRequest), pdfAccess],
    queryFn: async () => {
      if (pdfAccess) return await fetchPdfDataCompleteness(normalizedRequest, pdfAccess)
      return await fetchDataCompleteness(normalizedRequest)
    },
    staleTime: ONE_MINUTE_IN_MS * 5,
    enabled: enabled && hasDataCompletenessScope(normalizedRequest) && hasDataCompletenessDateRange(normalizedRequest),
  })
}
